/**
 * strategyApi.ts — TanStack Query hooks for the Strategia & Margini + Fornitori
 * & Storico domain (tender-api Step 6). Mirrors tenderApi.ts conventions: wire
 * DTOs (snake_case, api/schemas.py) → camelCase frontend models at this seam.
 *
 * Product decisions baked in (2026-07-25): RFQs PREPARE a price-scrubbed package
 * (no email); quotes are uploaded as files and read by AI; the margin uses the
 * prototype/delta method and is flagged provisional (the two team specs disagree).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { useApi } from "./api";

// ── Suppliers (Fornitori) ─────────────────────────────────────────────────────

export type SupplierType = "material" | "labor" | "both";

/** Italian labels for the supplier type. */
export const SUPPLIER_TYPE_LABEL: Record<SupplierType, string> = {
  material: "Materiale",
  labor: "Posa",
  both: "Materiale + Posa",
};

interface SupplierDto {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  categories: string[];
  supplier_type: SupplierType;
  verified: boolean;
  notes: string | null;
  quote_count: number;
  last_used: string | null;
  created_at: string;
  updated_at: string;
}

export interface Supplier {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  categories: string[];
  supplierType: SupplierType;
  verified: boolean;
  notes: string | null;
  quoteCount: number;
  lastUsed: string | null;
}

function toSupplier(d: SupplierDto): Supplier {
  return {
    id: d.id,
    name: d.name,
    email: d.email,
    phone: d.phone,
    categories: d.categories ?? [],
    supplierType: d.supplier_type,
    verified: d.verified,
    notes: d.notes,
    quoteCount: d.quote_count,
    lastUsed: d.last_used,
  };
}

export interface SupplierInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  categories?: string[];
  supplierType?: SupplierType;
  verified?: boolean;
  notes?: string | null;
}

function toSupplierBody(i: SupplierInput): Record<string, unknown> {
  return {
    name: i.name,
    email: i.email ?? null,
    phone: i.phone ?? null,
    categories: i.categories ?? [],
    supplier_type: i.supplierType ?? "both",
    verified: i.verified ?? false,
    notes: i.notes ?? null,
  };
}

export function useSuppliers(q?: string): UseQueryResult<Supplier[]> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "suppliers", q ?? ""],
    queryFn: async ({ signal }): Promise<Supplier[]> => {
      const res = await api.cmeinsight.get<{ suppliers: SupplierDto[] }>("/v1/suppliers", {
        signal,
        params: q ? { q } : undefined,
      });
      return res.suppliers.map(toSupplier);
    },
  });
}

export function useCreateSupplier(): UseMutationResult<Supplier, Error, SupplierInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SupplierInput): Promise<Supplier> => {
      const dto = await api.cmeinsight.post<SupplierDto>("/v1/suppliers", {
        body: toSupplierBody(input),
      });
      return toSupplier(dto);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tender", "suppliers"] });
    },
  });
}

export function useUpdateSupplier(): UseMutationResult<
  Supplier,
  Error,
  { id: string; input: Partial<SupplierInput> }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }): Promise<Supplier> => {
      const body: Record<string, unknown> = {};
      if (input.name !== undefined) body.name = input.name;
      if (input.email !== undefined) body.email = input.email;
      if (input.phone !== undefined) body.phone = input.phone;
      if (input.categories !== undefined) body.categories = input.categories;
      if (input.supplierType !== undefined) body.supplier_type = input.supplierType;
      if (input.verified !== undefined) body.verified = input.verified;
      if (input.notes !== undefined) body.notes = input.notes;
      const dto = await api.cmeinsight.patch<SupplierDto>(`/v1/suppliers/${id}`, { body: body });
      return toSupplier(dto);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tender", "suppliers"] });
    },
  });
}

export function useDeleteSupplier(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<void> => api.cmeinsight.del<void>(`/v1/suppliers/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tender", "suppliers"] });
    },
  });
}

export interface SupplierHistoryEntry {
  quoteId: string;
  projectId: string;
  projectName: string;
  supplierName: string;
  categoryName: string | null;
  materialCost: number | null;
  laborCost: number | null;
  total: number;
  eurUnit: number | null;
  unit: string | null;
  vsRegion: number | null;
  decidedAt: string;
}

interface SupplierHistoryDto {
  quote_id: string;
  project_id: string;
  project_name: string;
  supplier_name: string;
  category_name: string | null;
  material_cost: number | null;
  labor_cost: number | null;
  total: number;
  eur_unit: number | null;
  unit: string | null;
  vs_region: number | null;
  decided_at: string;
}

function toHistoryEntry(d: SupplierHistoryDto): SupplierHistoryEntry {
  return {
    quoteId: d.quote_id,
    projectId: d.project_id,
    projectName: d.project_name,
    supplierName: d.supplier_name,
    categoryName: d.category_name,
    materialCost: d.material_cost,
    laborCost: d.labor_cost,
    total: d.total,
    eurUnit: d.eur_unit,
    unit: d.unit,
    vsRegion: d.vs_region,
    decidedAt: d.decided_at,
  };
}

export function useSupplierHistory(q?: string): UseQueryResult<SupplierHistoryEntry[]> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "supplier-history", q ?? ""],
    queryFn: async ({ signal }): Promise<SupplierHistoryEntry[]> => {
      const res = await api.cmeinsight.get<{ entries: SupplierHistoryDto[] }>(
        "/v1/supplier-history",
        { signal, params: q ? { q } : undefined },
      );
      return res.entries.map(toHistoryEntry);
    },
  });
}

// ── Strategia table + margin ──────────────────────────────────────────────────

export type MakeBuy = "internal" | "external";

interface StrategyRowDto {
  category_id: string;
  code: string | null;
  name: string;
  /** Goal 55B, L-1: the sum of the amounts READ (null when every one is unread). */
  cme_cost: number | null;
  cme_unread?: number;
  mode: MakeBuy;
  internal: number;
  materials: number;
  work: number;
  discount: number;
  /** null when derived from a CME cost nobody could read (goal 55B, L-1). */
  planned_cost: number | null;
  delta: number | null;
  delta_pct: number | null;
  is_default: boolean;
}

export interface StrategyRow {
  categoryId: string;
  code: string | null;
  name: string;
  /** Goal 55B, L-1: the known part; null with `cmeUnread` > 0 when every amount is unread. */
  cmeCost: number | null;
  cmeUnread?: number;
  mode: MakeBuy;
  internal: number;
  materials: number;
  work: number;
  discount: number;
  plannedCost: number | null;
  /** null — «non determinabile» — when an amount behind the CME cost is unread. */
  delta: number | null;
  deltaPct: number | null;
  isDefault: boolean;
}

interface StrategyTotalsDto {
  cme: number | null;
  cme_unread?: number;
  expected: number | null;
  expected_unread?: number;
  delta: number | null;
  accepted_savings: number;
  margin_eur: number | null;
  margin_pct: number | null;
  target_pct: number;
  margin_provisional: boolean;
  margin_method: string;
}

export interface StrategyTotals {
  /** Goal 55B, L-1: known parts with their unread counts; the margin is null — «non
   * determinabile» — whenever an amount behind the CME is unread. */
  cme: number | null;
  cmeUnread?: number;
  expected: number | null;
  expectedUnread?: number;
  delta: number | null;
  acceptedSavings: number;
  marginEur: number | null;
  marginPct: number | null;
  targetPct: number;
  marginProvisional: boolean;
}

export interface StrategyTable {
  rows: StrategyRow[];
  totals: StrategyTotals;
}

interface StrategyTableDto {
  rows: StrategyRowDto[];
  totals: StrategyTotalsDto;
}

function toStrategyTable(d: StrategyTableDto): StrategyTable {
  return {
    rows: d.rows.map((r) => ({
      categoryId: r.category_id,
      code: r.code,
      name: r.name,
      cmeCost: r.cme_cost,
      cmeUnread: r.cme_unread ?? 0,
      mode: r.mode,
      internal: r.internal,
      materials: r.materials,
      work: r.work,
      discount: r.discount,
      plannedCost: r.planned_cost,
      delta: r.delta,
      deltaPct: r.delta_pct,
      isDefault: r.is_default,
    })),
    totals: {
      cme: d.totals.cme,
      cmeUnread: d.totals.cme_unread ?? 0,
      expected: d.totals.expected,
      expectedUnread: d.totals.expected_unread ?? 0,
      delta: d.totals.delta,
      acceptedSavings: d.totals.accepted_savings,
      marginEur: d.totals.margin_eur,
      marginPct: d.totals.margin_pct,
      targetPct: d.totals.target_pct,
      marginProvisional: d.totals.margin_provisional,
    },
  };
}

export function useStrategy(projectId: string | null): UseQueryResult<StrategyTable> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "project", projectId, "strategy"],
    enabled: !!projectId,
    queryFn: async ({ signal }): Promise<StrategyTable> => {
      const dto = await api.cmeinsight.get<StrategyTableDto>(`/v1/projects/${projectId}/strategy`, {
        signal,
      });
      return toStrategyTable(dto);
    },
  });
}

export interface CategoryStrategyInput {
  mode: MakeBuy;
  internal: number;
  materials: number;
  work: number;
  discount: number;
}

export function useUpsertCategoryStrategy(
  projectId: string,
): UseMutationResult<StrategyTable, Error, { categoryId: string; input: CategoryStrategyInput }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // One shared scope → these PUTs run STRICTLY IN SERIES. This is the only
    // mutation that writes its own response straight into the cache, so without
    // serialization two quick edits (flipping make/buy back and forth) can be
    // applied out of order: the slower earlier request lands last and both the
    // table and the margin snap back to the superseded value.
    scope: { id: `strategy-upsert-${projectId}` },
    mutationFn: async ({ categoryId, input }): Promise<StrategyTable> => {
      const dto = await api.cmeinsight.put<StrategyTableDto>(
        `/v1/projects/${projectId}/strategy/${categoryId}`,
        { body: input },
      );
      return toStrategyTable(dto);
    },
    onSuccess: (data) => {
      qc.setQueryData(["tender", "project", projectId, "strategy"], data);
    },
  });
}

// ── RFQ (prepared package) ────────────────────────────────────────────────────

export type RfqStatus = "draft" | "ready" | "closed";

interface RfqDto {
  id: string;
  project_id: string;
  category_id: string | null;
  category_code: string | null;
  category_name: string;
  recipient_supplier_ids: string[];
  subject: string;
  body: string;
  hide_unit_prices: boolean;
  include_drawings: boolean;
  status: RfqStatus;
  has_package: boolean;
  created_at: string;
  updated_at: string;
}

export interface Rfq {
  id: string;
  projectId: string;
  categoryId: string | null;
  categoryName: string;
  recipientSupplierIds: string[];
  subject: string;
  body: string;
  status: RfqStatus;
  hasPackage: boolean;
  createdAt: string;
}

function toRfq(d: RfqDto): Rfq {
  return {
    id: d.id,
    projectId: d.project_id,
    categoryId: d.category_id,
    categoryName: d.category_name,
    recipientSupplierIds: d.recipient_supplier_ids ?? [],
    subject: d.subject,
    body: d.body,
    status: d.status,
    hasPackage: d.has_package,
    createdAt: d.created_at,
  };
}

export interface RfqInput {
  categoryId: string;
  recipientSupplierIds: string[];
  subject: string;
  body: string;
  hideUnitPrices: boolean;
  includeDrawings: boolean;
}

export function useCreateRfq(projectId: string): UseMutationResult<Rfq, Error, RfqInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RfqInput): Promise<Rfq> => {
      const dto = await api.cmeinsight.post<RfqDto>(`/v1/projects/${projectId}/rfqs`, {
        body: {
          category_id: input.categoryId,
          recipient_supplier_ids: input.recipientSupplierIds,
          subject: input.subject,
          body: input.body,
          hide_unit_prices: input.hideUnitPrices,
          include_drawings: input.includeDrawings,
        },
      });
      return toRfq(dto);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "rfqs"] });
    },
  });
}

/** Fetch a short-lived presigned download URL for an RFQ's scrubbed package. */
export function useRfqPackageUrl(): UseMutationResult<string, Error, string> {
  const api = useApi();
  return useMutation({
    mutationFn: async (rfqId: string): Promise<string> => {
      const res = await api.cmeinsight.get<{ url: string }>(`/v1/rfqs/${rfqId}/package`);
      return res.url;
    },
  });
}

// ── Quotes (inbox) ────────────────────────────────────────────────────────────

export type QuoteStatus = "pending" | "accepted" | "rejected";
export type QuoteExtractionStatus = "manual" | "extracting" | "extracted" | "failed";

interface QuoteDto {
  id: string;
  project_id: string;
  rfq_id: string | null;
  supplier_id: string | null;
  supplier_name: string;
  category_id: string | null;
  category_code: string | null;
  category_name: string | null;
  material_cost: number | null;
  labor_cost: number | null;
  total: number;
  vs_region: number | null;
  eur_unit: number | null;
  unit: string | null;
  status: QuoteStatus;
  extraction_status: QuoteExtractionStatus;
  source_filename: string | null;
  extraction_error: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface Quote {
  id: string;
  projectId: string;
  supplierId: string | null;
  supplierName: string;
  categoryId: string | null;
  categoryName: string | null;
  materialCost: number | null;
  laborCost: number | null;
  total: number;
  vsRegion: number | null;
  status: QuoteStatus;
  extractionStatus: QuoteExtractionStatus;
  sourceFilename: string | null;
  extractionError: string | null;
}

function toQuote(d: QuoteDto): Quote {
  return {
    id: d.id,
    projectId: d.project_id,
    supplierId: d.supplier_id,
    supplierName: d.supplier_name,
    categoryId: d.category_id,
    categoryName: d.category_name,
    materialCost: d.material_cost,
    laborCost: d.labor_cost,
    total: d.total,
    vsRegion: d.vs_region,
    status: d.status,
    extractionStatus: d.extraction_status,
    sourceFilename: d.source_filename,
    extractionError: d.extraction_error,
  };
}

/** Poll the quotes inbox; keeps polling while any quote is still extracting. */
export function useQuotes(projectId: string | null): UseQueryResult<Quote[]> {
  const api = useApi();
  return useQuery<Quote[]>({
    queryKey: ["tender", "project", projectId, "quotes"],
    enabled: !!projectId,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.some((q) => q.extractionStatus === "extracting") ? 4000 : false;
    },
    queryFn: async ({ signal }): Promise<Quote[]> => {
      const res = await api.cmeinsight.get<{ quotes: QuoteDto[] }>(
        `/v1/projects/${projectId}/quotes`,
        { signal },
      );
      return res.quotes.map(toQuote);
    },
  });
}

export interface UploadQuoteInput {
  projectId: string;
  file: File;
  categoryId?: string | null;
  supplierId?: string | null;
  supplierName?: string | null;
}

/** Upload a supplier quote file → the backend AI-extracts the numbers (async). */
export function useUploadQuote(): UseMutationResult<Quote, Error, UploadQuoteInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      file,
      categoryId,
      supplierId,
      supplierName,
    }: UploadQuoteInput): Promise<Quote> => {
      const form = new FormData();
      form.append("file", file);
      if (categoryId) form.append("category_id", categoryId);
      if (supplierId) form.append("supplier_id", supplierId);
      if (supplierName) form.append("supplier_name", supplierName);
      const dto = await api.cmeinsight.post<QuoteDto>(`/v1/projects/${projectId}/quotes/upload`, {
        formData: form,
      });
      return toQuote(dto);
    },
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "quotes"] });
    },
  });
}

export interface QuoteUpdateInput {
  supplierId?: string | null;
  supplierName?: string;
  categoryId?: string | null;
  materialCost?: number | null;
  laborCost?: number | null;
  total?: number;
  vsRegion?: number | null;
  status?: QuoteStatus;
  notes?: string | null;
}

export function useUpdateQuote(
  projectId: string,
): UseMutationResult<Quote, Error, { id: string; input: QuoteUpdateInput }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }): Promise<Quote> => {
      const body: Record<string, unknown> = {};
      if (input.supplierId !== undefined) body.supplier_id = input.supplierId;
      if (input.supplierName !== undefined) body.supplier_name = input.supplierName;
      if (input.categoryId !== undefined) body.category_id = input.categoryId;
      if (input.materialCost !== undefined) body.material_cost = input.materialCost;
      if (input.laborCost !== undefined) body.labor_cost = input.laborCost;
      if (input.total !== undefined) body.total = input.total;
      if (input.vsRegion !== undefined) body.vs_region = input.vsRegion;
      if (input.status !== undefined) body.status = input.status;
      if (input.notes !== undefined) body.notes = input.notes;
      const dto = await api.cmeinsight.patch<QuoteDto>(`/v1/quotes/${id}`, { body: body });
      return toQuote(dto);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "quotes"] });
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "strategy"] });
    },
  });
}

export function useDeleteQuote(projectId: string): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<void> => api.cmeinsight.del<void>(`/v1/quotes/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "quotes"] });
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "strategy"] });
    },
  });
}
